import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Car, Calendar, Map as MapIcon, ShoppingCart, DollarSign, Download,
  Heart, User as UserIcon, Wrench, Home, ArrowRight, CornerDownLeft, Command as CmdIcon,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'nav-home',        label: 'Home',              hint: '/',              to: '/',            icon: Home },
  { id: 'nav-marketplace', label: 'Marketplace',       hint: 'Browse vehicles',to: '/marketplace', icon: Car },
  { id: 'nav-rides',       label: 'Ride share',        hint: 'Find a ride',    to: '/rides',       icon: Car },
  { id: 'nav-events',      label: 'Events',            hint: 'Meets & shows',  to: '/events',      icon: Calendar },
  { id: 'nav-maps',        label: 'Maps & amenities',  hint: 'Find spots',     to: '/maps',        icon: MapIcon },
  { id: 'nav-pricing',     label: 'Pricing',           hint: 'Plans',          to: '/pricing',     icon: DollarSign },
  { id: 'nav-download',    label: 'Get the app',       hint: 'iOS / Android',  to: '/download',    icon: Download },
  { id: 'nav-sell',        label: 'Sell a vehicle',    hint: 'List for free',  to: '/sell',        icon: ShoppingCart },
  { id: 'nav-host',        label: 'Host an event',     hint: 'Rally the scene',to: '/host-event',  icon: Calendar },
  { id: 'nav-watchlist',   label: 'Saved listings',    hint: 'Watchlist',      to: '/watchlist',   icon: Heart },
  { id: 'nav-profile',     label: 'My profile',        hint: 'Signed-in dashboard', to: '/profile', icon: UserIcon },
  { id: 'nav-billing',     label: 'Billing & invoices',hint: 'Manage plan',    to: '/billing',     icon: Wrench },
];

function money(n) { return `$${(n || 0).toLocaleString()}`; }

export default function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = React.useState('');
  const [vehicles, setVehicles] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const navigate = useNavigate();

  // Reset on open, focus input
  React.useEffect(() => {
    if (!open) return;
    setQ('');
    setVehicles([]);
    setActive(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Debounced vehicle search
  React.useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (!query) { setVehicles([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/vehicles`, { params: { q: query, limit: 8 } });
        if (!cancelled) { setVehicles(data.vehicles || []); setLoading(false); }
      } catch {
        if (!cancelled) { setVehicles([]); setLoading(false); }
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const query = q.trim().toLowerCase();
  const filteredNav = React.useMemo(() => {
    if (!query) return NAV_ITEMS;
    return NAV_ITEMS.filter((n) =>
      n.label.toLowerCase().includes(query) || n.hint.toLowerCase().includes(query)
    );
  }, [query]);

  const items = React.useMemo(() => [
    ...filteredNav.map((n) => ({ kind: 'nav', ...n })),
    ...vehicles.map((v) => ({ kind: 'vehicle', id: `v-${v.id}`, label: v.title, hint: `${money(v.price)} · ${v.city || ''}`, to: `/vehicles/${v.id}`, photo: v.photos?.[0] })),
  ], [filteredNav, vehicles]);

  // Reset active when items change
  React.useEffect(() => { setActive(0); }, [items.length]);

  // Keep active row scrolled into view
  React.useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  const runItem = React.useCallback((item) => {
    if (!item) return;
    onOpenChange(false);
    navigate(item.to);
  }, [navigate, onOpenChange]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onOpenChange(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(items.length - 1, i + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); return; }
    if (e.key === 'Enter')     { e.preventDefault(); runItem(items[active]); return; }
  };

  if (!open) return null;

  const navGroup = items.filter((i) => i.kind === 'nav');
  const vehGroup = items.filter((i) => i.kind === 'vehicle');

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-start justify-center px-4 pt-24 sm:pt-32"
      data-testid="command-palette"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        data-testid="cmdk-backdrop"
      />
      <div className="relative w-full max-w-2xl bg-[#0d0d0d] hairline shadow-2xl am-dropdown-in">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 hairline-b">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, makes, models…"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
            data-testid="cmdk-input"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex am-pill !py-0.5 !px-1.5 !text-[10px]">Esc</kbd>
        </div>

        {/* List */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2" data-testid="cmdk-list">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="cmdk-empty">
              {loading ? 'Searching…' : 'No matches. Try a make, model, or a page name.'}
            </div>
          ) : (
            <>
              {navGroup.length > 0 && (
                <Group title="Navigate">
                  {navGroup.map((it, i) => {
                    const idx = i;
                    const Icon = it.icon || ArrowRight;
                    return (
                      <Row
                        key={it.id}
                        idx={idx}
                        active={active === idx}
                        onClick={() => runItem(it)}
                        onHover={() => setActive(idx)}
                        icon={<Icon className="w-4 h-4 text-[#ff2a2a]" />}
                        label={it.label}
                        hint={it.hint}
                        testId={`cmdk-item-${it.id}`}
                      />
                    );
                  })}
                </Group>
              )}
              {vehGroup.length > 0 && (
                <Group title={`Vehicles${loading ? ' · searching…' : ''}`}>
                  {vehGroup.map((it, i) => {
                    const idx = navGroup.length + i;
                    return (
                      <Row
                        key={it.id}
                        idx={idx}
                        active={active === idx}
                        onClick={() => runItem(it)}
                        onHover={() => setActive(idx)}
                        icon={it.photo
                          ? <img src={it.photo} alt="" className="w-8 h-6 object-cover" />
                          : <div className="w-8 h-6 bg-[#131313] hairline grid place-items-center"><Car className="w-3 h-3 text-zinc-600"/></div>}
                        label={it.label}
                        hint={it.hint}
                        testId={`cmdk-item-${it.id}`}
                      />
                    );
                  })}
                </Group>
              )}
              {q.trim() === '' && (
                <div className="px-4 pt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  Tip: type a make (Porsche), model (GT-R), or a page name.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 hairline-t text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigate</span>
            <span className="inline-flex items-center gap-1"><Kbd><CornerDownLeft className="w-2.5 h-2.5"/></Kbd> open</span>
          </div>
          <span className="inline-flex items-center gap-1"><Kbd><CmdIcon className="w-2.5 h-2.5"/></Kbd><Kbd>K</Kbd> anywhere</span>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="mb-1">
      <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({ idx, active, onClick, onHover, icon, label, hint, testId }) {
  return (
    <button
      data-idx={idx}
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      onMouseMove={onHover}
      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition-colors ${active ? 'bg-[#ff2a2a]/10 text-white' : 'text-zinc-300 hover:bg-white/5'}`}
      data-testid={testId}
    >
      <div className="shrink-0 w-8 grid place-items-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{label}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 truncate">{hint}</div>
      </div>
      {active && <ArrowRight className="w-3.5 h-3.5 text-[#ff2a2a] shrink-0" />}
    </button>
  );
}

function Kbd({ children }) {
  return <kbd className="hairline px-1.5 py-0.5 text-[10px] text-zinc-400 bg-[#0a0a0a] leading-none">{children}</kbd>;
}
